// Shared DTOs + request schemas used by BOTH apps/web and apps/api.
// Keeping these here makes packages/shared-types the single source of truth for the wire
// contract (no duplicate types in web or api).
import { z } from "zod";
import {
  DomainSchema,
  SelectedStatusSchema,
  PreprintStatusSchema,
  ReviewerStatusSchema,
  RefereeStatusSchema,
  RoleSchema,
  type Domain,
  type SelectedStatus,
  type PreprintStatus,
  type ReviewerStatus,
  type RefereeStatus,
  type Role,
  type CoiStatus,
  type CoiVerdict,
  type CoiPipelineState,
  type CoiReasonCode,
  COI_VERDICTS,
} from "./enums.js";

// ── Identity ─────────────────────────────────────────────────────────────────
export interface CurrentUser {
  email: string;
  name: string;
  roles: Role[];
  domains: Domain[];
  active: boolean;
}

// ── AE Details reviewer-profile sub-types (spec §5a.1) ───────────────────────
// Parsed from the markdown/text the Airtable snapshot stores, so no data migration is required
// (parsing happens at the packages/db mapper boundary; the web app consumes the typed shapes).

/**
 * A "model"/exemplar reviewer. Its base (name + links) is parsed from the `Peer Reviewer Model #N`
 * markdown; July-14 FR-P9 adds the full proposal-ready fields inline (email, affiliation,
 * sub-discipline, best-match paper) so the deck captures everything a reviewer proposal needs and
 * the "perfected" model reviewer can be **auto-proposed when the preprint is Selected**. The
 * structured fields live in the pitch key `Model Reviewers Structured` (a per-slot JSON array),
 * overlaid on the parsed string at the db-mapper boundary.
 */
export interface ModelReviewer {
  /** Which slot this came from (1|2|3); lets the AE annotate/label the card. */
  slot: number;
  name: string;
  /** Institutional / faculty profile link (non-PubMed). */
  profileUrl?: string;
  /** PubMed publications link. */
  pubmedUrl?: string;
  // ── FR-P9: full proposal-ready info captured inline next to the name ──
  /** Reviewer email — required to auto-propose this model reviewer at Selected. */
  email?: string;
  /** University / institutional affiliation. */
  affiliation?: string;
  /** Sub-discipline (drives the proposal justification, mirrors the evaluate form). */
  subdiscipline?: string;
  /** Best-match paper title (the single anchor paper — mirrors the evaluate form). */
  bestMatchPaperTitle?: string;
  /** Best-match paper URL/DOI (R-013/R-015 parity with the evaluate form → `doiBestMatch`). */
  bestMatchUrl?: string;
  /** WO-7/21 Track G: submission-portal parity fields. */
  secondaryAffiliation?: string;
  highestDegree?: string;
  otherDegree?: string;
  titleRole?: string;
  otherTitles?: string;
  /** The verbatim snapshot string (fallback rendering + round-trip safety). */
  raw: string;
}

/** FR-P9: the pitch-JSON key holding the per-slot structured model-reviewer array. */
export const MODEL_REVIEWERS_STRUCTURED_KEY = "Model Reviewers Structured";

/** WO-7/22 Track E: AE-seeded expertise phrases for the invite justification, stored on
 *  `selected.pitch` as a newline-separated list (~3 options). Shown from To Pitch(Editorial)
 *  (same stage as Model Peer Reviewers); do-not-present. Propose form offers them as
 *  "Because of your expertise in …" picks. */
export const AE_SUBDISCIPLINES_KEY = "AE Suggested Subdisciplines";

/** Jul-31 pitch-deck design: the one specific question a reviewer must answer about this
 *  preprint. Lives in More Context (do-not-present); stored on `selected.pitch`. */
export const KEY_REVIEWER_QUESTION_KEY = "Key Reviewer Question";

/** Parse the word-box value (newlines, commas, or semicolons) into trimmed options. */
export function aeSubdisciplines(pitch: Record<string, unknown>): string[] {
  const raw = pitch[AE_SUBDISCIPLINES_KEY];
  if (typeof raw !== "string") return [];
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** R-017 (SoftR parity): the paper-level COI answer, stored on `selected.pitch`. Unset = the
 *  question hasn't been answered; the → To Pitch(Editorial) advance refuses until it is. */
export const PAPER_COI_KEY = "Paper COI";
export type PaperCoiAnswer = "yes" | "no";

export function paperCoiAnswer(pitch: Record<string, unknown>): PaperCoiAnswer | null {
  const v = pitch[PAPER_COI_KEY];
  return v === "yes" || v === "no" ? v : null;
}

/** FR-P9: the editable structured half of a model reviewer, stored per slot in the pitch JSON. */
export interface ModelReviewerFields {
  slot: number;
  name?: string;
  email?: string;
  affiliation?: string;
  /** WO-7/21 Track G: submission-portal parity fields. */
  secondaryAffiliation?: string;
  highestDegree?: string;
  otherDegree?: string;
  title?: string;
  otherTitles?: string;
  subdiscipline?: string;
  bestMatchPaperTitle?: string;
  bestMatchUrl?: string;
  profileUrl?: string;
  pubmedUrl?: string;
}

/** AE annotation on an OpenAlex related work. `null` = un-annotated (`[RELATED/ NOT RELATED]`). */
export type RelatedVerdict = "RELATED" | "NOT RELATED" | null;
export interface RelatedWork {
  url: string;
  verdict: RelatedVerdict;
}

// ── Views (read models returned by the API) ──────────────────────────────────
export interface SelectedView {
  id: string;
  title: string;
  doi: string | null;
  authors: string | null;
  affiliations: string | null;
  domain: Domain | null;
  gatesFunded: boolean;
  /** `Selected.Team` — backs the R-005 Find Preprint team filter. */
  team: string | null;
  status: SelectedStatus;
  assigneeEmail: string | null;
  /** Resolved from Users at list time; null when unassigned or unknown. */
  assigneeName: string | null;
  /** Airtable `Date` on the Selected row — preprint publication/posting date for discovery cards. */
  publishedDate: string | null;
  /** Truncated abstract preview for discovery cards (from linked Preprint when available). */
  abstractBlurb: string | null;
  /** WO-7/21 Track D: the FULL abstract — set only by the single-row endpoint (list stays light). */
  abstract?: string | null;
  domainRanking: number | null;
  /**
   * Altmetric Attention Score (SoftR/Airtable). Null when unknown — common for older rows.
   * Discovery sort uses this only for preprints published within the last 14 days.
   */
  altmetricScore: number | null;
  /** Prophy reviewer-generation / refinement status ("Current Refinement Status" in Softr). */
  proposalStatus: string | null;
  /** Multi-rater Input Ranking: per-rater map (email → rank) + the computed average. */
  rankings: Record<string, number>;
  avgRanking: number | null;
  /** Pitch-deck payload (lossless JSON of all deck fields; urgency lives in `Urgent`). */
  pitch: Record<string, unknown>;
  aeChecks: Record<string, boolean>;
  preprintId: string | null;
  // ── July-8 critical-path gates (FR-N2 / FR-N3) ──
  /** FR-N2: email of the AE who finalized the deck; null until the Finalize control is set. */
  finalizedBy: string | null;
  /** FR-N2: when it was finalized (ISO). Cleared on any later deck edit — re-finalize required. */
  finalizedAt: string | null;
  /** FR-N3: when Refine Concepts last completed successfully (ISO). Null = advance is blocked. */
  refinedAt: string | null;
  // ── AE Details reviewer-profile half, parsed from `pitch` at the db mapper boundary ──
  /** Model Peer Reviewers (`Peer Reviewer Model #1/#2/#3`), non-empty slots only. */
  modelReviewers: ModelReviewer[];
  /** OpenAlex `Related Works`, each with its AE `RELATED`/`NOT RELATED` annotation. */
  relatedWorks: RelatedWork[];
  /** Prophy `Most Similar Articles` search URL, or null. */
  mostSimilarArticlesUrl: string | null;
}

export interface PreprintView {
  id: string;
  selectedId: string | null;
  title: string;
  doi: string | null;
  domain: Domain | null;
  rank: number | null;
  /**
   * The GLOBAL lifecycle status, and the only label any viewer sees (July-8 FR-N5, which revises
   * FR-L10's viewer-relative relabel — a status must not shift depending on who is looking).
   * "Is this one mine?" is answered by the **Mini team (N)** badge (`miniTeamEmails`) instead.
   * FR-J9 *access* scoping is unchanged and still applied server-side.
   */
  status: PreprintStatus;
  outreachStatus: string | null;
  abstract: string | null;
  authorEmail: string | null;
  miniTeamEmails: string[];
  /** WO-7/23 Track D: when the mini-team's two-week window opened (ISO), or null. */
  miniTeamStartedAt: string | null;
  /** Derived from `miniTeamStartedAt` — drives the "expiring soon" tag + homepage hazard. */
  miniTeamPhase: MiniTeamPhase;
  /** Derived close of the two-week window (ISO), or null. */
  miniTeamExpiresAt: string | null;
  assignedEditorEmail: string | null;
  /** Display name of the assigned AE (resolved from Users at the API), or null (FR-K11). */
  assignedEditorName: string | null;
  greenlitCount: number;
  proposedCount: number;
  /** Proposals awaiting AE quality check — complete + status `Unchecked` (July-7 FR-K11). */
  uncheckedCount: number;
  invitesRequired: number | null;
  inviteRound: number | null;
  reviewsReceived: number;
  publishedUrl: string | null;
  blueSkyUrl: string | null;
}

export interface RefereeView {
  id: string;
  name: string;
  affiliations: string | null;
  orcid: string | null;
  pubMedLink: string | null;
  hIndex: number | null;
  citationsCount: number | null;
  status: RefereeStatus;
  preprintId: string | null;
}

export interface ProposalView {
  id: string;
  preprintId: string | null;
  refereeId: string | null;
  firstName: string | null;
  lastName: string | null;
  reviewerEmail: string | null;
  affiliation: string | null;
  secondaryAffiliation: string | null;
  highestDegree: string | null;
  /** Optional additional degrees (SoftR "Other Degree"); label omits "(optional)". */
  otherDegree: string | null;
  title: string | null;
  /** Optional additional titles (SoftR "Other Title(s)"); label omits "(optional)". */
  otherTitles: string | null;
  /** Legacy: justification when no institutional profile link was provided. */
  profileJustification: string | null;
  subdiscipline: string | null;
  bestMatchPaperTitle: string | null;
  /** Best-match paper URL/DOI (R-015) — `reviewer_proposals.doi_best_match`. */
  doiBestMatch: string | null;
  justification: string | null;
  // July-7 FR-K12: the full QC card — links + proposing student identity travel with the view.
  pubMedLink: string | null;
  linkToProfile: string | null;
  linkToPublications: string | null;
  studentName: string | null;
  status: ReviewerStatus;
  /** Out-of-band resolution, e.g. "Unable to Evaluate" when a claim-limit release parked the
   *  claim (COI-12) — status itself stays "Unchecked" because QC never saw the row. */
  resolveStatus: string | null;
  coiStatus: CoiStatus;
  /** COI-10 axis 1 — has the engine run? (`not_checked` → `checking` → `checked`). */
  coiPipelineState: CoiPipelineState;
  coiVerdict: CoiVerdict | null;
  /** COI-17 reason codes (snake_case). Falls back to the single legacy reason when absent. */
  coiReasons: CoiReasonCode[];
  coiSummary: string | null;
  affiliationCoi: string | null;
  /** Student-declared affiliation-COI checkbox (07a §1) — distinct from engine evidence. */
  affiliationCoiFlag: boolean;
  affiliationCoiJustification: string | null;
  /** COI-14: justification collected when the reviewer email is not institutional. */
  emailJustification: string | null;
  backupJustification: string | null;
  proposalSource: string | null;
  // COI-11 evidence (persisted for audit, shown on the reviewer row).
  commonInstitutions: string | null;
  prophyScore: number | null;
  prophyAuthorUrl: string | null;
  coiComment: string | null;
  /** True when the verdict came from the shadow Prophy backend — advisory only (COI-16). */
  coiAdvisory: boolean;
  qcComments: string | null;
  /** Senior-AE-facing internal note attached on Double Check (July-7 FR-K14). */
  qcInternalNote: string | null;
  /** Student's reply to QC feedback (July-7 FR-K16). */
  studentReply: string | null;
  /** AE/QC who currently has this row claimed in the Quality Check queue (Frida-QC R-008). */
  qcClaimedByEmail: string | null;
  qcClaimedByName: string | null;
  qcEmail: string | null;
  /** The junior QC whose Invite call awaits senior confirmation (Frida-QC R-013 assist banner). */
  qcTrainingEmail: string | null;
  /** Proposal `Team` — backs the Frida-QC R-002 team filter. */
  team: string | null;
  domain: Domain | null;
  proposerEmail: string | null;
  /** When the proposal was created (ISO) — backs the WO-7/22 Track G date-of-proposal filter. */
  dateOfProposal: string | null;
  /** WO-7/22 Track F: the student's Contest-COI justification ("PROFI is wrong because …").
   *  Non-null = contested; unlocks Part 2 and is surfaced to the AE in Quality Check. */
  coiContestJustification: string | null;
  /** Part-2 profile form not yet submitted — hidden from AE Quality Check (July-7 FR-K15). */
  incomplete: boolean;
  hIndex: number | null;
  backupFor: string | null;
  /** True while a COI job is in flight (submitted, verdict not yet returned) — show "Checking…". */
  coiPending: boolean;
  /** True until COI resolves clear — the evaluate form stays locked (COI-5/FR-2.8). */
  evaluateLocked: boolean;
}

// ── July-14 role dashboards (FR-P1 / FR-P2) ──────────────────────────────────
/** A compact preprint card on a dashboard column. */
export interface DashboardPreprintCard {
  selectedId: string | null;
  preprintId: string | null;
  title: string;
  /** The lifecycle status to badge (selection status or pipeline status, per column). */
  status: string;
  domain: Domain | null;
  /** Greenlit reviewer count (Active column only). */
  greenlitCount?: number;
  /** WO-7/22 Track A: proposed count beside greenlit on active/mini-team student buckets. */
  proposedCount?: number;
  /** Proposals awaiting the AE's quality check — the third count chip on the AE Productivity
   *  Shepherding rows, and what the auto-hold ratio is computed against. */
  uncheckedCount?: number;
  /** WO-7/30 Track A: movement since the viewer's PREVIOUS login, so an AE can see a queue is
   *  actually progressing. Both are "arrived since" counts derived from `dateOfProposal` — see
   *  the AE dashboard route for why that is the honest reading of a delta on a current-state
   *  count. Absent (not 0) when there is no baseline yet, i.e. first-ever login. */
  proposedSince?: number;
  uncheckedSince?: number;
  /** WO-7/21 Track A: mini-team size (Productivity detail, Active column). */
  miniTeamCount?: number;
  /** WO-7/23 Track A/D: this card is a mini-team preprint (shown inside Active), and where its
   *  two-week window stands — `expiring` raises the homepage hazard sign. */
  isMiniTeam?: boolean;
  miniTeamPhase?: MiniTeamPhase;
  miniTeamExpiresAt?: string | null;
  publishedDate?: string | null;
}

export interface AeDashboard {
  editor: { email: string; name: string | null; team?: string | null; domains?: Domain[] };
  /** True when the requester is viewing SOMEONE ELSE's dashboard (Central/Admin — FR-P1). */
  viewingOther: boolean;
  /** WO-7/21 Track A: the period the in-period metrics cover (echoed from ?from/?to). */
  period?: { from: string; to: string };
  /** WO-7/30 Track A: the baseline the per-card `*Since` deltas are measured from — the viewer's
   *  previous login (ISO), or null on a first-ever login, when no delta is shown. */
  deltaSince?: string | null;
  kpis: {
    greenlitsThisWeek: number;
    /** WO-7/21 Track A: greenlits inside the requested period (defaults to the last 4 weeks). */
    greenlitsInPeriod?: number;
    greenlitsToDate: number;
    proposals: number;
    greenlitProposals: number;
  };
  preprints: {
    toPitchTeam: DashboardPreprintCard[];
    toPitchEditorial: DashboardPreprintCard[];
    selected: DashboardPreprintCard[];
    active: DashboardPreprintCard[];
  };
  /** WO-7/21 Track A: led preprints past reviewer selection (On Hold / Dormant / Invited /
   *  Complete) — the Productivity detail's reviewer-pipeline list. */
  pipeline?: DashboardPreprintCard[];
  /** WO-7/21 Track A: every QC decision this AE stamped in the period (Productivity QC tab). */
  qcDecisions?: { date: string | null; reviewer: string; preprintTitle: string; status: ReviewerStatus }[];
  /** AE Productivity v2: every proposal on this AE's led preprints, counted by status — the
   *  "Proposal outcomes" donut. All statuses are present, including the two that sit outside the
   *  proposed denominator (`NOT_PROPOSED_STATUSES`), so the chart can say so rather than hide them. */
  proposalOutcomes?: { status: ReviewerStatus; count: number }[];
  /** WO-7/22 Track I: the AE's attention-needed surface — fixable/fixed reviewers awaiting the
   *  AE plus pending mini-team join requests (each accepted in one click). */
  attention?: {
    /** Complete proposals on led preprints sitting in Fixable (student fixing) or Fixed
     *  (awaiting the AE's re-check). */
    fixable: number;
    fixed: number;
    joinRequests: { preprintId: string; preprintTitle: string; email: string; name: string | null }[];
  };
  /** domains/team back the R-018 roster filters (course isn't captured on Users; team/domain are). */
  students: {
    email: string;
    name: string | null;
    proposals: number;
    greenlits: number;
    /** Complete proposals still waiting on AE QC (Unchecked). */
    unchecked?: number;
    /** Stefano: Invite ÷ (proposed − unchecked), 0–100. */
    greenlitRate?: number;
    domains: Domain[];
    team: string | null;
    /** WO-7/22 Track I (Claire): the student's pitches now in flight, by gate. */
    toPitchTeam?: number;
    toPitchEditorial?: number;
    /** Titles behind the counts (compact disclosure on the roster row). */
    pitches?: { title: string; gate: "Team" | "Editorial"; selectedId: string }[];
    /** `Users.active` — the AE Productivity roster defaults to Active only, so a deactivated
     *  student stops skewing the roster without vanishing from the record entirely. */
    active?: boolean;
  }[];
}

export interface StudentDashboard {
  student: { email: string; name: string | null };
  kpis: {
    slotsUsed: number;
    slotsCap: number;
    greenlitsToDate: number;
    proposals: number;
    greenlitRate: number; // 0–100
    needsAttention: number; // Fixable reviewers
  };
  preprints: {
    claimed: DashboardPreprintCard[];
    toPitchTeam: DashboardPreprintCard[];
    /** SoftR-parity R-003: the Editorial half of the 2-pitch group (was folded into inPipeline). */
    toPitchEditorial: DashboardPreprintCard[];
    /** Post-pitch editorial pipeline (Selected) — editorial pitches now live in toPitchEditorial.
     *  WO-7/22 Track A relabels this bucket "In queue" in the UI (in queue to be proposed /
     *  to become active); the wire key stays for compatibility. */
    inPipeline: DashboardPreprintCard[];
    activeForMe: DashboardPreprintCard[];
    /** WO-7/22 Track A: Dormant pipeline preprints where the student is on the mini-team —
     *  cards carry team-level proposed/greenlit counts so students see mini-team progress. */
    miniTeam: DashboardPreprintCard[];
  };
  /** Sorted by attention (R-001): Fixable → COI Detected → Reject → rest. */
  reviewers: {
    id: string;
    name: string;
    preprintTitle: string;
    status: ReviewerStatus;
    /** Deep-link target for the homepage "Waiting on you" rows (null if the preprint is gone). */
    preprintId: string | null;
    /** Contested COIs are a deliberate extra step, not a homepage to-do (WO-7/23 Track E) —
     *  the "Waiting on you" list skips COI Detected rows where this is true. */
    coiContested: boolean;
  }[];
}

/** One AE row on the Central/Admin Home roster (FR-P1 / US-P2) and the WO-7/21 Productivity
 *  overview (grouped by `team`, with the full pipeline-status columns). */
export interface StaffAeRow {
  email: string;
  name: string | null;
  domains: Domain[];
  /** Users.team — the Productivity overview's grouping bucket. */
  team?: string | null;
  greenlitsThisWeek: number;
  /** Greenlits inside the requested ?from/?to period (defaults to the last 4 weeks). */
  greenlitsInPeriod?: number;
  greenlitsToDate: number;
  proposals: number;
  greenlitProposals: number;
  activeCount: number;
  toPitchTeam: number;
  toPitchEditorial: number;
  selected: number;
  /** WO-7/21 Track A: remaining led-preprint pipeline-status columns. */
  onHold?: number;
  dormant?: number;
  invited?: number;
  complete?: number;
  /** Productivity Tab v3: led preprints with at least one mini-team member. */
  miniTeam?: number;
}

/** One student row on the Team / Student productivity roster (Productivity Tab v3).
 *  Proposal buckets are complete rows whose dateOfProposal falls in the requested period,
 *  keyed by CURRENT status; `proposed` applies the truly-proposed rule (WO-7/21 Track B). */
export interface StaffStudentRow {
  email: string;
  name: string | null;
  team: string | null;
  domains: Domain[];
  proposed: number;
  unchecked: number;
  greenlit: number;
  rejected: number;
  /** Current Selected rows assigned to the student, by pitch gate. */
  toPitchTeam: number;
  toPitchEditorial: number;
}

/** GET /dashboard/students — staff-only Team / Student productivity roster. Participation
 *  units and meeting/workshop attendance from the design have no data source yet and are
 *  deliberately absent (the UI shows explicit "not tracked" placeholders). */
export interface StudentsDashboard {
  period: { from: string; to: string };
  students: StaffStudentRow[];
}

/** Central / Admin Home: AE roster + rollup KPIs. Drill-down uses GET /dashboard/ae?editor=. */
export interface StaffDashboard {
  viewer: { email: string; name: string | null; role: "Central" | "Admin" };
  kpis: {
    aeCount: number;
    greenlitsThisWeek: number;
    activePreprints: number;
    proposals: number;
  };
  aes: StaffAeRow[];
}

// ── Student "My Productivity" (iter2, design repo 2026-07-21-ae-webapp-demo) ─
/** One milestone on a badge. `tracked: false` = the requirement lives outside the webapp
 *  (quizzes, meeting attendance) — it renders unchecked with a "tracked elsewhere" hint and
 *  can never flip until that source lands. */
export interface BadgeMilestone {
  label: string;
  done: boolean;
  tracked: boolean;
}

export interface BadgeProgress {
  /** Stable key; the badge art ships at /badges/badge-<key>.png. */
  key: string;
  name: string;
  /** Done milestones / total milestones. */
  done: number;
  total: number;
  complete: boolean;
  milestones: BadgeMilestone[];
}

export interface BadgeTrack {
  name: string;
  badges: BadgeProgress[];
}

/** GET /dashboard/my-productivity — student productivity + badges.
 *  Self-scoped by default; AE-and-above may pass ?student=<email> to view another student.
 *  Canonical layout: design repo docs/designs/2026-07-21-ae-webapp-demo/. */
export interface MyProductivity {
  student: { email: string; name: string | null; team: string | null; domains: Domain[] };
  /** True when an AE/Central/Admin is viewing another student's progress. */
  viewingOther?: boolean;
  /** Echo of ?from/?to. `from: null` = all-time (the default — "Term totals"). */
  period: { from: string | null; to: string };
  totals: {
    /** Pitch-gate passages derived from CURRENT Selected statuses (no transition log in
     *  snapshot data): past To Pitch(Team) ⇒ team-pitched, past To Pitch(Editorial) ⇒
     *  editorial-pitched. Not period-scoped. */
    pitchesTeam: number;
    pitchesEditorial: number;
    /** Truly proposed in period (Student Reject / COI Detected excluded — WO-7/21 Track B). */
    proposals: number;
    /** Invites in period (by dateOfQc, like the AE dashboard's greenlitsInPeriod). */
    greenlits: number;
    greenlitRate: number; // 0–100, of in-period proposals
  };
  /** Complete-proposal counts by status, all-time (the Reviewers funnel is current-state). */
  reviewerStatusCounts: Partial<Record<ReviewerStatus, number>>;
  /** All-time truly-proposed total — the funnel's 100% bar. */
  reviewerProposedTotal: number;
  /** Current statuses of my Selected rows (the Pitches funnel). */
  pitchFunnel: { claimed: number; toPitchTeam: number; toPitchEditorial: number; selected: number };
  badgeTracks: BadgeTrack[];
  /** WO-7/30 Track D: the 4-week attendance log. */
  attendance: AttendanceLog;
}

/**
 * WO-7/30 Track D [43:52–46:12]: Zoom-derived meeting attendance, surfaced as the student's
 * 4-week log. This webapp only ever CONSUMES it — ingestion belongs to the attendance-dashboard
 * thread (design repo docs/designs/2026-07-24-attendance-dashboard/), and `source: "none"` is what
 * the API returns until that pipeline lands.
 *
 * Shaped to Noah's locked rules (that design's `attendance-rules.md`): the two in-scope meeting
 * types are the weekly team meeting and the global workshop, the expected denominator is one of
 * each per week, and a STUDENT sees attended/missed only. No minutes, no thresholds, no duration
 * math may reach this contract — those stay on the AE/management side.
 */
export interface AttendanceLog {
  /** "none" until the Zoom pipeline is wired; "zoom" once weeks carry real marks. */
  source: "none" | "zoom";
  /** Most recent week first. Empty while `source` is "none". */
  weeks: AttendanceWeek[];
}

export interface AttendanceWeek {
  /** Monday of the week, ISO date (YYYY-MM-DD). */
  weekStart: string;
  /** The weekly team meeting expected of this student, and whether they attended. `title` names
   *  WHICH meeting (e.g. "RR\ID Berkeley Weekly Team Meetings (BCS)"). Null = none expected. */
  teamMeeting: { attended: boolean; title: string | null } | null;
  /** The one global workshop expected per week. Null = none expected that week. */
  globalWorkshop: { attended: boolean } | null;
}

// ── Request schemas (validated at the API boundary with Zod) ─────────────────
export const AssignPreprintSchema = z.object({ selectedId: z.string() });

export const UpdateSelectedStatusSchema = z.object({
  status: SelectedStatusSchema,
  domain: DomainSchema.optional(),
  aeChecks: z.record(z.boolean()).optional(),
});

export const SavePitchSchema = z.object({
  pitch: z.record(z.unknown()),
  domain: DomainSchema.optional(),
});

// AE Details partial deck save — every key optional; merges into `pitch`, never clobbers
// unedited keys (FR-1.16). `pitch` itself is optional so a domain-only correction is valid.
export const PatchDeckSchema = z
  .object({
    pitch: z.record(z.unknown()).optional(),
    domain: DomainSchema.optional(),
  })
  .refine((b) => b.pitch !== undefined || b.domain !== undefined, {
    message: "provide at least one of pitch or domain",
  });

// AE annotates OpenAlex `Related Works` RELATED / NOT RELATED (FR-1.14).
export const RelatedWorksAnnotateSchema = z.object({
  items: z
    .array(
      z.object({
        url: z.string().url(),
        verdict: z.enum(["RELATED", "NOT RELATED"]).nullable(),
      }),
    )
    .min(1),
});

// Refine Concepts / Updated Reviewers (FR-1.15) — async Prophy regeneration trigger.
export const RefineSchema = z
  .object({ mode: z.enum(["concepts", "reviewers"]).default("concepts") })
  .default({ mode: "concepts" });

// "Propose a Reviewer" embedded form, step 1 (FR-1.17 / spec §5a.3).
export const ProposeReviewerStep1Schema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  affiliation: z.string().min(1),
  inviteBackup: z.boolean().default(false),
  affiliationCoi: z.boolean().default(false),
});

// July-14 FR-P5: 5 selected per domain per week; rank 1 = Active, ranks 2–5 = Dormant queue.
export const RANK_MAX = 5;
export const RankPreprintSchema = z.object({ rank: z.number().int().min(1).max(RANK_MAX) });

export const SetPreprintStatusSchema = z.object({ status: PreprintStatusSchema });

// July-14 FR-P6: Central/Admin force-change any preprint status, bypassing the transition guard.
// A short reason is recorded in the audit trail.
export const ForceStatusSchema = z.object({
  status: PreprintStatusSchema,
  reason: z.string().trim().optional(),
});

// July-14 FR-P7: a non-member requests to join a mini-team; the AE approves/adds them manually.
export const RequestJoinSchema = z.object({ message: z.string().trim().max(500).optional() });

// WO-7/22 Track I: the AE's one-click accept on a pending join request (dashboard + editor).
export const AcceptJoinSchema = z.object({ email: z.string().email() });

// WO-7/22 Track F: a student contests a PROFI COI reject — the justification is required and is
// surfaced to the AE in Quality Check ("this person was rejected by PROFI COI; explain why they
// should still proceed").
export const ContestCoiSchema = z.object({ justification: z.string().trim().min(1).max(2000) });

// July-2 (FR-J10): mini-team cap raised to 5, rendered as 5 fixed slots in the UI.
export const MINI_TEAM_MAX = 5;
export const MiniTeamSchema = z.object({
  emails: z.array(z.string().email()).max(MINI_TEAM_MAX),
});

// ── WO-7/23 Track D: mini-team lifecycle [20:25–22:06] ───────────────────────
// A mini-team gets two weeks to reach the queue-jump threshold. Week 1 is normal; at the start of
// week 2 the box shows an "expiring soon" tag, members get one automatic email, and the student
// homepage raises a hazard sign. Past the full window the team has expired.
export const MINI_TEAM_WARN_DAYS = 7;
export const MINI_TEAM_EXPIRY_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Where a mini-team sits in its two-week window. `none` = no team / no start stamp. */
export type MiniTeamPhase = "none" | "active" | "expiring" | "expired";

/** ISO timestamp when the mini-team's two-week window closes (null when it never started). */
export function miniTeamExpiresAt(startedAt: string | null | undefined): string | null {
  const t = startedAt ? Date.parse(startedAt) : NaN;
  return Number.isFinite(t) ? new Date(t + MINI_TEAM_EXPIRY_DAYS * DAY_MS).toISOString() : null;
}

/**
 * Phase of a mini-team's window. `nowMs` is injectable so the server, the UI, and tests all agree
 * (and so tests don't have to wait two weeks).
 */
export function miniTeamPhase(
  startedAt: string | null | undefined,
  nowMs: number = Date.now(),
): MiniTeamPhase {
  const t = startedAt ? Date.parse(startedAt) : NaN;
  if (!Number.isFinite(t)) return "none";
  const elapsedDays = (nowMs - t) / DAY_MS;
  if (elapsedDays >= MINI_TEAM_EXPIRY_DAYS) return "expired";
  if (elapsedDays >= MINI_TEAM_WARN_DAYS) return "expiring";
  return "active";
}

// July-7 (FR-K4/K5): a person holds at most 3 preprints that are assigned to them and not yet
// pitched (status `Taken`); submitting to Pitch Team frees the slot. Preprints they LEAD as the
// assigned AE are excluded — shepherding is uncapped.
export const ASSIGNMENT_CAP = 3;

// July-7 (FR-K10) / July-14 (FR-P4): when a preprint's AE-unchecked proposals exceed this fraction
// of its proposed total, it auto-moves ACTIVE → ON HOLD (off the propose list) until QC catches up;
// it returns to ACTIVE once the ratio drops back under the threshold.
//
// ⚠️ FR-P4 THRESHOLD UNRESOLVED — CONFIRM WITH ANGEL. This is the single named config point for the
// auto-hold. FR-K10 (July-7) set it at ~60% (0.6); Angel's July-14 example ("100 proposed, 90
// unchecked → on hold") implies ~0.9. The two were never reconciled in the check-in — see
// docs/prd/09-user-journeys.md §1. We keep 0.6 as the shipped value (unchanged behaviour) and
// deliberately expose it as ONE constant rather than hard-coding a number at the call site; change
// this single line once Angel confirms. Do NOT scatter the literal.
export const ON_HOLD_UNCHECKED_RATIO = 0.6;
// The rule targets a QC BACKLOG ("~60%" of a real queue, e.g. 40 unchecked of 70 proposed) — with
// no floor the very first proposal (1/1 = 100%) would hold every active preprint. Below this many
// proposals the ratio isn't meaningful and the trigger stays off.
export const ON_HOLD_MIN_PROPOSED = 5;

// ── July-14 ranking → activation thresholds (FR-P5, reconciling FR-2.4) ───────
// A DORMANT mini-team preprint that reaches this many greenlits JUMPS the activation queue and
// goes ACTIVE ahead of its rank (Angel: "a mini team jumps the queue with 20 green-lit reviewers").
export const MINI_TEAM_QUEUE_JUMP_GREENLIT = 20;
// When an ACTIVE preprint reaches this many greenlits, the NEXT-ranked DORMANT preprint in its
// domain is activated ("Once it's active, it hits 40, the next one will be made active"). The
// active preprint does NOT revert (FR-P3) — activation is additive, so a domain can briefly hold
// more than one ACTIVE preprint (this is the intended reconciliation of FR-2.4's one-active rule
// with the queue-jump; see docs/prd/09-user-journeys.md §1 drift notes).
export const NEXT_RANK_ACTIVATE_GREENLIT = 40;

export const ClaimRefereeSchema = z.object({ refereeId: z.string(), preprintId: z.string() });

// COI-12: "you can only claim two AI suggested reviewers at a time; claiming a third returns
// the previous ones as 'Unable to Evaluate'" — the oldest claim is released.
export const AI_CLAIM_LIMIT = 2;

// COI-first proposal form (July-2 FR-J12): First/Last/Email/Affiliation + the two checkboxes.
// The ordered walls (author, duplicate/recent, same-affiliation→Backup, Prophy stub) run
// server-side BEFORE the proposal row is created.
export const CreateProposalSchema = z.object({
  preprintId: z.string(),
  refereeId: z.string().nullable().optional(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  reviewerEmail: z.string().email(),
  affiliation: z.string().min(1),
  backupReviewer: z.boolean().default(false),
  affiliationCoi: z.boolean().default(false),
});

export const CoiSubmitSchema = z.object({ proposalId: z.string() });

export const CoiWebhookSchema = z.object({
  jobId: z.string(),
  proposalId: z.string(),
  verdict: z.enum(COI_VERDICTS),
  reason: z.string().optional(),
  summary: z.string().optional(),
  affiliationCoi: z.string().optional(),
});

// ── "Check COI" submit (COI-9, SoftR parity 07a §1) ──────────────────────────
/** Free-mail domains that do NOT count as institutional (COI-14). Anything else — .edu,
 *  university/lab/company domains — is treated as institutional. Heuristic on purpose:
 *  false negatives just require a justification, never block a submit outright. */
const FREE_MAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "hotmail.com", "outlook.com",
  "live.com", "msn.com", "aol.com", "icloud.com", "me.com", "mail.com", "protonmail.com",
  "proton.me", "gmx.com", "gmx.de", "yandex.com", "yandex.ru", "qq.com", "163.com", "126.com",
  "sina.com", "naver.com", "hanmail.net", "zoho.com",
]);

export function isInstitutionalEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split("@")[1] ?? "";
  if (!domain) return false;
  return !FREE_MAIL_DOMAINS.has(domain);
}

/** The "Check COI" multi-step form (07a §1). Conditional requirements mirror the SoftR form:
 *  non-institutional email ⇒ justification (COI-14); backup ⇒ "Invite Backup For…" + its
 *  justification (COI-13); affiliation-COI checkbox ⇒ its justification. */
export const CheckCoiProposalSchema = z
  .object({
    preprintId: z.string().min(1),
    firstName: z.string().trim().min(1),
    lastName: z.string().trim().min(1),
    reviewerEmail: z.string().trim().email(),
    emailJustification: z.string().trim().optional(),
    affiliation: z.string().trim().min(1),
    backupReviewer: z.boolean().default(false),
    backupForId: z.string().optional(),
    backupJustification: z.string().trim().optional(),
    affiliationCoi: z.boolean().default(false),
    affiliationCoiJustification: z.string().trim().optional(),
    /** "Student" from the Check COI form; "Manual" from "Manually add suggested reviewer". */
    proposalSource: z.enum(["Student", "Manual"]).default("Student"),
  })
  .superRefine((body, ctx) => {
    if (!isInstitutionalEmail(body.reviewerEmail) && !body.emailJustification) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["emailJustification"],
        message: "Justification required for a non-institutional email (COI-14)",
      });
    }
    if (body.backupReviewer && !body.backupForId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["backupForId"],
        message: "Select which reviewer this is a backup for (COI-13)",
      });
    }
    if (!body.backupReviewer && body.backupForId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["backupForId"],
        message: "backupForId only applies to backup reviewers (COI-13)",
      });
    }
    if (body.backupReviewer && !body.backupJustification) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["backupJustification"],
        message: "Backup justification required (COI-13)",
      });
    }
    if (body.affiliationCoi && !body.affiliationCoiJustification) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["affiliationCoiJustification"],
        message: "Justification for the affiliation conflict of interest is required",
      });
    }
  });

export const EvaluateProposalSchema = z.object({
  /** WO-7/21 Track G: affiliation is re-confirmable at Part 2 (captured at Part 1). */
  affiliation: z.string().optional(),
  secondaryAffiliation: z.string().trim().min(1),
  highestDegree: z.string().trim().min(1),
  /** Optional; SoftR "Other Degree". */
  otherDegree: z.string().optional(),
  title: z.string().trim().min(1),
  /** Optional; SoftR "Other Title(s)". */
  otherTitles: z.string().optional(),
  subdiscipline: z.string().min(1), // required (FR-2.11)
  linkToProfile: z.string().trim().min(1),
  /** Legacy escape hatch — ignored; profile link is required. */
  profileJustification: z.string().trim().optional(),
  publicationLink: z.string().trim().min(1),
  bestMatchPaperTitle: z.string().min(1),
  /** R-015: link to the best-match paper (URL or DOI) — required to complete a proposal;
   *  persisted to `reviewer_proposals.doi_best_match`. */
  bestMatchUrl: z.string().trim().min(1),
  justificationOverride: z.string().optional(),
});

export const QcSetStatusSchema = z.object({
  status: ReviewerStatusSchema,
  comments: z.string().optional(),
});

export const SetFixedSchema = z.object({});

// July-7 (FR-K16): the student's reply to QC feedback ("I've made the correction").
export const QcReplySchema = z.object({ comment: z.string().trim().min(1) });

// July-7 (FR-K16): student Edit on "My Quality Checks" — patch their own reviewer profile to
// apply the fix. Fields are optional for partial senior/owner patches; the Edit UI requires the
// same profile fields as Part 2 evaluate before "Mark as Fixed".
export const EditProposalProfileSchema = z
  .object({
    affiliation: z.string().optional(),
    secondaryAffiliation: z.string().optional(),
    highestDegree: z.string().optional(),
    otherDegree: z.string().optional(),
    title: z.string().optional(),
    otherTitles: z.string().optional(),
    subdiscipline: z.string().optional(),
    linkToProfile: z.string().optional(),
    publicationLink: z.string().optional(),
    bestMatchPaperTitle: z.string().optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: "provide at least one field to update",
  });

export const JanewayHandoffSchema = z.object({ preprintId: z.string() });

export const RecordReviewSchema = z.object({
  preprintId: z.string(),
  reviewerEmail: z.string().email(),
  decision: z.enum(["accept", "reject", "withdraw", "submitted"]),
});

export const PublishSchema = z.object({
  preprintId: z.string(),
  pubPubUrl: z.string().url().optional(),
  blueSkyUrl: z.string().url().optional(),
  blurb: z.string().optional(),
});

export interface DevSnapshotOption {
  id: string;
  dirName: string;
  label: string;
  description: string;
}

export const DevLoadSnapshotSchema = z.object({
  snapshot: z.string().min(1),
});

export const DevLoginSchema = z.object({
  email: z.string().email(),
  snapshot: z.string().min(1).optional(),
});

export type AssignPreprintBody = z.infer<typeof AssignPreprintSchema>;
export type CreateProposalBody = z.infer<typeof CreateProposalSchema>;
export type CheckCoiProposalBody = z.infer<typeof CheckCoiProposalSchema>;
export type CoiSubmitBody = z.infer<typeof CoiSubmitSchema>;
export type CoiWebhookBody = z.infer<typeof CoiWebhookSchema>;
export type EvaluateProposalBody = z.infer<typeof EvaluateProposalSchema>;
export type QcSetStatusBody = z.infer<typeof QcSetStatusSchema>;
export type PatchDeckBody = z.infer<typeof PatchDeckSchema>;
export type RelatedWorksAnnotateBody = z.infer<typeof RelatedWorksAnnotateSchema>;
export type ProposeReviewerStep1Body = z.infer<typeof ProposeReviewerStep1Schema>;

// Re-export commonly paired enum schemas for convenience.
export { DomainSchema, PreprintStatusSchema, ReviewerStatusSchema, RefereeStatusSchema, RoleSchema };

/** Derive the canonical invite justification (FR-2.12). */
export function deriveJustification(subdiscipline: string, bestMatchPaperTitle: string): string {
  return `Because of your expertise in ${subdiscipline}, and especially your paper "${bestMatchPaperTitle}", we think you'd be a terrific reviewer for this preprint.`;
}
